import { useEffect, useRef, useState } from 'react'
import AppHeader from '../../components/shell/AppHeader'
import SupervisorNav from '../../components/supervisor/SupervisorNav'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import EmptyState from '../../components/shell/EmptyState'
import StatusChip, { type RecordState } from '../../components/agent/StatusChip'
import { CATEGORY_FILL, type Category, type DayEntry } from '../../components/agent/scheduleDay'
import { supabase } from '../../lib/supabase'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { PAY_PERIOD, PAY_PERIOD_DATES } from '../agent/payPeriod19'
import { PAY_PERIOD_20, PAY_PERIOD_20_DATES } from '../agent/payPeriod20'
import { getBoardRoster, type BoardRoster, type BoardRosterRow } from './boardRosterStore'
import { getSupervisorTeams } from './useSupervisorTeams'
import { cellKey, keepCurrentKeyFormat, parseCellKey, type Period } from './planningBoardCellKey'
import type { Team } from './useSupervisorTeams'
import { getCurrentTeamId, setCurrentTeamId as persistCurrentTeamId } from './currentTeamStore'
import TeamSwitcher from '../../components/supervisor/TeamSwitcher'
import { BOARD_CODES } from './boardCodes'
import { SHIFT_TEMPLATE } from '../agent/scheduleDefaults'
import { getStaffingTargets, getTargetCatalog, type StaffingTarget } from './staffingTargetStore'
import { coverageFor, WHOLE_SHIFT, type CoverageFinding } from './coverageAnalysis'
import { AUTOSAVE_DELAY_MS } from './planningBoardAutosave'
import { getBoardDraft, migrateOverridesForApprovedLeave, saveBoardDraft, type BoardOverrideEntry } from './planningBoardDraftStore'
import { appendBoardHistory, getBoardHistory, historyForCell, type BoardHistoryEntry } from './planningBoardHistoryStore'
import {
  getPublications,
  overridesMatch,
  publishBoard,
  wireEntriesFor,
  type PublicationVersion,
  getPublishedPeriods,
  reopenPayPeriod,
} from './publishedScheduleStore'
import { getTeamLeaveRequests, type TeamLeaveRequest } from './leaveRequestQueue'
import { getTeamAvailability, type TeamAvailability } from './availabilityBoardStore'
import { schedulingServiceEnabled } from '../../lib/features'
import { workConflictFor, workWindowOf, type AvailabilityConflict } from './availabilityOverlay'
import { getApprovedScheduleDays, type ApprovedScheduleDay } from './approvedScheduleStore'

/**
 * docs/design/style-guide.md's "Planning board": employees as rows, dates
 * as columns, the employee column frozen, Week 1 and Week 2 separated by a
 * heavier rule. Selecting a cell, a Shift range, or a Ctrl/Cmd-click
 * scatter of individually toggled cells that share no row or column in
 * common (activate's own toggle) all show the same action bar, applying a
 * status, assignment, or leave code to everything selected at once,
 * either with one of the six quick-select buttons or by typing a code
 * from boardCodes.ts into the autocomplete field, per
 * docs/design/journey-map.md's "Type a code straight into a focused cell."
 * the draft save persists every change made across both pay periods at once;
 * publication with versions is its own, later capability, so there is no
 * publish yet.
 *
 * The rows are the selected team's real employees (employee_assignments)
 * and each cell a real schedule_entries row, loaded by boardRosterStore.ts;
 * the team switcher lists only the teams the signed-in account supervises.
 * The period toggle switches between the current pay period and the next
 * one. In either, an employee with no entries in the period reads
 * "Unsubmitted" per docs/design/journey-map.md's "Identify unsubmitted
 * agent schedules for the period," and their cells are not selectable,
 * since there is no schedule there yet to select.
 *
 * Status and assignment filters highlight a matching cell rather than
 * hiding or dimming the rest: hiding would mean removing table cells,
 * which breaks every row's column alignment, and dimming every
 * non-matching cell's category fill away reads as though the schedule
 * itself changed rather than just the view of it. A matching cell keeps
 * its own category fill and gains an outline on top of it. The fill,
 * the outline, and selection's own ring all live on the `td` itself
 * rather than its button: a table cell's own height is always the row's
 * real height, set by the tallest cell in the row, but a percentage
 * height on a child inside a `td` does not reliably resolve to that same
 * height, so a button asked to stretch to fill its cell instead stops at
 * its own shorter content height, leaving a gap in every row next to a
 * taller one. Outline and ring are two different CSS properties rather
 * than two different classes of the same one deliberately, so both show
 * at once on a cell that is both selected and filter-matching.
 *
 * The team toggle (TeamSwitcher.tsx) switches which of Sgt. Diaz's teams
 * the whole board is scoped to, per docs/product/personas.md's "A
 * supervisor owns one or more teams." Everything session-scoped (the
 * draft, publications, and cell history) is keyed by team as well as by
 * pay period, so switching teams never mixes one team's overrides into
 * another's; switching asks first if the team being left has unsaved
 * changes, the same guard the Dashboard link uses. There is still no
 * shift filter, since this pilot's whole field office shares the one
 * shift template; nothing exists yet for it to meaningfully narrow. There
 * is no qualification filter either, since no qualification catalog
 * exists yet.
 *
 * A leave request the supervisor has approved in the approval inbox shows
 * on the board as a leave cell on every date it covers, per
 * docs/design/journey-map.md's "Apply approved agent requests to the
 * board," ahead of whatever the base roster or a board override would
 * otherwise show there; Approve with supervisor modification covers only
 * the dates the supervisor actually kept (leaveRequestQueue.ts's own
 * TeamLeaveRequest.dates, narrowed from the request's own full range once
 * decided), which may be fewer than what was requested. It shows,
 * and stays selectable and editable (isCellEditable, cellEditablePredicate),
 * even on an employee's own unsubmitted cell in the next pay period: the
 * leave was decided on its own merits, independent of whether the rest of
 * that period has been submitted yet, so it is real, actionable board
 * content the moment it is approved rather than something a supervisor
 * has to wait on the rest of the period to touch. Every other cell in
 * that same row stays unselectable until the employee submits the period.
 * A real schedule submission (scheduleSubmissionQueue.ts, decided through
 * scheduleDecisionStore.ts's own decideScheduleSubmission) works the same
 * way once approved: approvedScheduleStore.ts reads the day-by-day
 * schedule_entries rows muster.submit_schedule already wrote at
 * submission time and shows them on the board a cell at a time, ahead of
 * the base roster, the same precedence and the same editability exemption
 * approved leave gets (appliedSchedule alongside appliedLeave throughout).
 * Returning or denying a submission deletes that period's own
 * schedule_entries rows (muster.decide_schedule_submission), so that
 * employee's row for whichever period it covers reads "Unsubmitted" and
 * stops being selectable again, until they resubmit.
 *
 * A cell's own shift time reformats to 12-hour with the account menu's
 * Time format setting (src/lib/timeFormat.ts), on the visible label and
 * its aria-label alike. The type-to-enter code field's own catalog
 * (boardCodes.ts) stays in its written, 24-hour form regardless, since
 * that text is what the supervisor types and matches against, not
 * something being displayed back.
 *
 * Undo, change preview, and cell history (docs/design/journey-map.md's
 * "Undo recent changes," "View the changes before saving," and "See who
 * entered or changed each item, and open the history of a cell") are all
 * session-scoped rather than backed by an audit table. Undo is an in-memory
 * stack of prior override snapshots, one push per Apply a status or Apply
 * code action, cleared the moment the board is saved or published, the
 * same boundary docs/design/journey-map.md's daily assignment board
 * describes for its own Undo. Review changes compares the working
 * `overrides` against whatever was last saved and lists every cell that
 * differs, across both pay periods at once, the same scope the draft save
 * itself covers. Cell history (planningBoardHistoryStore.ts) is append-only
 * and keyed by cell, so re-applying a status to the same cell adds an entry
 * rather than replacing the last one; it only ever reaches the supervisor
 * who made the change, in the browser they made it in.
 *
 * Publication with versions and amendments (publishedScheduleStore.ts,
 * muster.schedule_versions since 20260924010000_schedule_publication.sql)
 * is real: publish writes the team's current overrides for the period
 * into schedule_entries and a numbered schedule_versions snapshot, who
 * published and when, in one transaction, per
 * docs/product/scheduling-model.md's "A published schedule is a
 * version." A team's own last published version is also the board's new
 * base layer (publishedSnapshotByCell, entryFor), read back the next
 * time anyone loads this team's board, in this browser or any other, so
 * publication outlives the session that made it. Publish also saves the
 * working draft, so the two never disagree the moment it runs. A
 * period's status chip (docs/design/style-guide.md's status vocabulary)
 * reads Draft before the first publish, Published immediately after, and
 * Amended once a further edit makes the period's overrides differ from
 * its latest version again; publishing again from Amended appends the
 * next version, and notifies every current member of the team
 * (muster.publish_schedule's own notification insert). Publishing always
 * succeeds once a supervisor and a period are known:
 * docs/design/journey-map.md's blocking-conflict gate does not apply
 * yet, since conflict detection is not built.
 */

const CATEGORY_OPTIONS: { key: Category; label: string; patchLabel: string }[] = [
  { key: 'work', label: 'Work', patchLabel: '0700–1500' },
  { key: 'off', label: 'Off', patchLabel: 'Regular day off' },
  { key: 'leave', label: 'Leave', patchLabel: 'Annual leave' },
  { key: 'training', label: 'Training', patchLabel: '0800–1600 · Training' },
  { key: 'detail', label: 'Detail', patchLabel: 'Detailed out' },
  { key: 'unavailable', label: 'Unavailable', patchLabel: 'Unavailable' },
]

// Every distinct assignment on a work day across the given rows: every
// option here is guaranteed to match at least one real cell, per
// docs/product/scheduling-model.md's "Assignment | For work | The
// assignment code", rather than a dropdown entry nothing on the board ever
// has. assignmentOptions below adds whatever a supervisor has since
// applied that was not already there.
function workAssignmentsOf(rows: BoardRow[]): string[] {
  return rows
    .flatMap((member) => member.days)
    .filter((day) => day.category === 'work' && day.detail)
    .map((day) => day.detail as string)
}

function mostCommonAssignment(assignments: string[]): string | undefined {
  const counts = new Map<string, number>()
  for (const assignment of assignments) {
    counts.set(assignment, (counts.get(assignment) ?? 0) + 1)
  }
  // Ties (equally common) settle alphabetically, so the result is
  // deterministic rather than dependent on array order.
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0]
}

// Applying Work, or the assignment-less Scheduled workday code, to a cell
// that was not already a work entry has no real assignment to carry
// forward: the cell's own detail means something else entirely there (a
// training topic, "Full day" for leave, nothing at all for a day off), and
// carrying that value over would write it into the assignment field as
// though it were one. The fallback is the employee's own most common
// assignment across both loaded pay periods, then the team's, and no
// assignment at all for a team whose schedules carry none.
function defaultAssignmentFor(memberId: string, currentRows: BoardRow[], nextRows: BoardRow[]): string | undefined {
  const allRows = [...currentRows, ...nextRows]
  const own = workAssignmentsOf(allRows.filter((member) => member.id === memberId))
  return mostCommonAssignment(own) ?? mostCommonAssignment(workAssignmentsOf(allRows))
}

type Cell = { row: number; col: number }
type Override = { category: Category; label: string; detail?: string }
type BoardDate = { date: string; weekday: string; day: number; month: string }
type BoardRow = BoardRosterRow

const CURRENT_DATES: BoardDate[] = PAY_PERIOD_DATES

function rowsFor(period: Period, currentRows: BoardRow[], nextRows: BoardRow[]): BoardRow[] {
  return period === 'current' ? currentRows : nextRows
}

function datesFor(period: Period): BoardDate[] {
  return period === 'current' ? CURRENT_DATES : PAY_PERIOD_20_DATES
}

export type AppliedLeave = { label: string; fullDay: boolean; startTime?: string; endTime?: string }

// Every date an approved leave request covers, keyed by employee and
// date, so entryFor can show it on the matching cell regardless of
// whether that cell already has a base entry, an override, or nothing at
// all (an unsubmitted next-period cell). A plain function of
// leaveRequests rather than a value closed over the component's own
// state, so the mount effect and switchTeam below can each build the
// same lookup for whichever leave requests are actually loaded at that
// point, before the main render's own copy exists yet. TeamLeaveRequest's
// own dates is already narrowed to Approve with modification's own
// subset once decided (leaveRequestQueue.ts), so this reads it directly
// rather than reaching for a separate modifiedDates field. Carries
// fullDay and its own times rather than assuming every approved leave is
// a full day: entryFor only actually removes someone from the day's own
// schedule (a category override to 'leave') when it is.
function approvedLeaveByCellFor(leaveRequests: TeamLeaveRequest[]): Map<string, AppliedLeave> {
  const approvedLeaveByCell = new Map<string, AppliedLeave>()
  for (const item of leaveRequests) {
    if (item.state !== 'approved') continue
    for (const date of item.dates) {
      approvedLeaveByCell.set(`${item.employeeId}|${date}`, {
        label: item.leaveType,
        fullDay: item.fullDay,
        startTime: item.startTime,
        endTime: item.endTime,
      })
    }
  }
  return approvedLeaveByCell
}

// migrateOverridesForApprovedLeave (planningBoardDraftStore.ts) purges a
// stale override on every cell this map names, on the premise that
// entryFor's own leave-vs-override precedence would otherwise hide it
// under an override that predates the approval. That premise only holds
// for a full-day leave, the only kind entryFor ever lets override a
// cell: passing a partial-day entry through would purge an override the
// board should keep showing exactly as the supervisor left it.
function fullDayOnly(approvedLeaveByCell: Map<string, AppliedLeave>): Map<string, { label: string }> {
  const fullDay = new Map<string, { label: string }>()
  for (const [key, value] of approvedLeaveByCell) {
    if (value.fullDay) fullDay.set(key, { label: value.label })
  }
  return fullDay
}

// Every cell a team's latest published version, for either period,
// actually covers, re-keyed from that version's own cellKey-based
// overrides (period|employeeId|column, publishedScheduleStore.ts's own
// PublicationVersion shape) to employee and date, the same key shape
// approvedLeaveByCell and approvedScheduleByCell already use. A plain
// function of publishedVersions rather than a value closed over the
// component's own state, so the mount effect and switchTeam below can
// each build the same lookup for whichever versions loadTeamData just
// resolved, before the main render's own copy exists yet. Only the
// latest version per period matters: an earlier one is history, not
// something a cell should still show.
function publishedSnapshotByCellFor(publishedVersions: Record<Period, PublicationVersion[]>): Map<string, Override> {
  const publishedSnapshotByCell = new Map<string, Override>()
  for (const period of ['current', 'next'] as Period[]) {
    const versions = publishedVersions[period]
    if (versions.length === 0) continue
    const latest = versions[versions.length - 1]
    const dates = datesFor(period)
    for (const entry of latest.overrides) {
      const { employeeId, col } = parseCellKey(entry.key)
      const date = dates[col]
      if (!date) continue
      publishedSnapshotByCell.set(`${employeeId}|${date.date}`, { category: entry.category, label: entry.label, detail: entry.detail })
    }
  }
  return publishedSnapshotByCell
}

// A cell is editable when its own row is (the ordinary case: a submitted,
// non-withdrawn row), or, independent of that, when an approved leave
// request or an approved schedule submission covers it: either was
// decided on its own merits, not on whether the rest of the period has
// been submitted yet, so it needs to be an editable part of the staffing
// picture the moment it is approved, the same as any other real entry,
// rather than sitting on the board read-only until the employee happens
// to submit the rest of the period.
//
// A published snapshot deliberately plays no part here, unlike those
// two: it is only ever a claim about what a row showed at some past
// moment, and a withdrawal has to be able to make that claim stale
// (publicationStatusFor below reads that staleness as Amended) rather
// than the old snapshot keeping a withdrawn row selectable as though
// nothing changed.
function cellEditablePredicate(
  rows: BoardRow[],
  dates: BoardDate[],
  approvedLeaveByCell: Map<string, AppliedLeave>,
  approvedScheduleByCell: Map<string, ApprovedScheduleDay>,
) {
  return (row: number, col: number): boolean => {
    const member = rows[row]
    if (!member) return false
    const date = dates[col]
    if (!date || !member.teamDates.has(date.date)) return false
    if (member.editable) return true
    const cellKeyName = `${member.id}|${date.date}`
    return approvedLeaveByCell.has(cellKeyName) || approvedScheduleByCell.has(cellKeyName)
  }
}

// Scans every row and then every column within it for the first editable
// cell, not just column 0: an approved leave request can be the only
// editable cell in an otherwise unsubmitted or withdrawn row, at any
// column, so a search confined to column 0 can land initial focus on a
// disabled cell while the actual editable one sits further along the same
// row, leaving a keyboard user with no tabIndex={0} cell to tab into.
function firstEditableCellAt(
  rows: BoardRow[],
  dates: BoardDate[],
  approvedLeaveByCell: Map<string, AppliedLeave>,
  approvedScheduleByCell: Map<string, ApprovedScheduleDay>,
): Cell {
  const isEditable = cellEditablePredicate(rows, dates, approvedLeaveByCell, approvedScheduleByCell)
  for (let row = 0; row < rows.length; row += 1) {
    for (let col = 0; col < dates.length; col += 1) {
      if (isEditable(row, col)) return { row, col }
    }
  }
  return { row: 0, col: 0 }
}

// Scans from `from` toward `direction` for the next editable row, skipping
// a withdrawn or otherwise unsubmitted one along the way rather than
// stopping dead the moment one is adjacent: a withdrawal can now land on
// any row, not only the top or bottom of the roster, so arrow-key
// navigation has to route around one wherever it falls. Returns `from`
// itself, unmoved, once there is nowhere further editable that way (or
// `direction` is 0, an unrelated horizontal move, which never changes
// rows and so never risks looping on a row `isEditable` keeps rejecting).
function nextEditableRow(rowCount: number, from: number, direction: number, isEditable: (row: number) => boolean): number {
  if (direction === 0) return from
  let row = from
  while (true) {
    const next = row + direction
    if (next < 0 || next >= rowCount) return row
    row = next
    if (isEditable(row)) return row
  }
}

// A rectangle covers every cell between two corners regardless of which
// corner the selection started from, the same as a spreadsheet's own
// click-then-shift-click range. A cell the isEditable predicate rejects
// contributes nothing: a withdrawn or unsubmitted one reads Unsubmitted
// and is not selectable on its own, so a range that merely passes
// through it on the way to an editable cell on the far side should not
// silently pick up hidden overrides on a schedule the board is not
// currently showing as part of the staffing picture; an approved-leave
// cell within an otherwise unsubmitted row is the one exception, and
// isEditable already accounts for it.
function rectangleKeys(
  period: Period,
  a: Cell,
  b: Cell,
  rows: BoardRow[],
  isEditable: (row: number, col: number) => boolean,
): Set<string> {
  const rowStart = Math.min(a.row, b.row)
  const rowEnd = Math.max(a.row, b.row)
  const colStart = Math.min(a.col, b.col)
  const colEnd = Math.max(a.col, b.col)
  const keys = new Set<string>()
  for (let row = rowStart; row <= rowEnd; row += 1) {
    for (let col = colStart; col <= colEnd; col += 1) {
      if (!isEditable(row, col)) continue
      keys.add(cellKey(period, rows[row].id, col))
    }
  }
  return keys
}

function InfoIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <line x1="12" y1="8" x2="12" y2="8" />
    </svg>
  )
}

const WEEK_1_LAST_COLUMN = 6

function sameOverrides(a: Map<string, Override>, b: Map<string, Override>): boolean {
  if (a.size !== b.size) return false
  for (const [key, value] of a) {
    const other = b.get(key)
    if (!other || other.category !== value.category || other.label !== value.label || other.detail !== value.detail) return false
  }
  return true
}

export default function SupervisorPlanningBoardPage() {
  const identity = useProfileIdentity()
  const { format } = useTimeFormat()
  const [userId, setUserId] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const [teams, setTeams] = useState<Team[]>([])
  const [currentTeamId, setCurrentTeamIdState] = useState<string>('')
  const [boardRoster, setBoardRoster] = useState<BoardRoster>({ current: [], next: [] })
  const [period, setPeriod] = useState<Period>('current')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [anchor, setAnchor] = useState<Cell | null>(null)
  const [focused, setFocused] = useState<Cell>({ row: 0, col: 0 })
  const [overrides, setOverrides] = useState<Map<string, Override>>(new Map())
  // What is actually persisted, as of the last successful the draft save (or
  // Publish, which saves too): the baseline pendingChanges below diffs
  // `overrides` against, and what hasUnsavedChanges itself is derived from.
  const [savedOverrides, setSavedOverrides] = useState<Map<string, Override>>(new Map())
  // What Review changes lists changes against: the draft as it was when the
  // board opened, or when the period was last published. The draft saves
  // itself as the supervisor works, so the last save is not a baseline a review
  // could use.
  const [baselineOverrides, setBaselineOverrides] = useState<Map<string, Override>>(new Map())
  const [undoStack, setUndoStack] = useState<Map<string, Override>[]>([])
  const [codeInput, setCodeInput] = useState('')
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [saveFailed, setSaveFailed] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [selectionHelpOpen, setSelectionHelpOpen] = useState(false)
  const [historyOpenFor, setHistoryOpenFor] = useState<string | null>(null)
  const [boardHistory, setBoardHistory] = useState<BoardHistoryEntry[]>([])
  const [supervisorName, setSupervisorName] = useState<string | null>(null)
  const [publishedVersions, setPublishedVersions] = useState<Record<Period, PublicationVersion[]>>({
    current: [],
    next: [],
  })
  const [publishFailed, setPublishFailed] = useState(false)
  // The server's reason for a refused publish, when it gave one.
  const [publishMessage, setPublishMessage] = useState<string | null>(null)
  const [publishConfirmation, setPublishConfirmation] = useState<{ period: Period; version: number; at: Date } | null>(
    null,
  )
  const [statusFilters, setStatusFilters] = useState<Set<Category>>(new Set())
  const [assignmentFilter, setAssignmentFilter] = useState<string | null>(null)
  // The team's open assignments, offered as chips to drag onto a day, the one
  // being dragged, the cell it is over, and what the last drop did (for a live
  // region, since a drop has no other announcement).
  // The periods this team has published, which are read-only until reopened.
  const [lockedPeriods, setLockedPeriods] = useState<Record<Period, boolean>>({ current: false, next: false })
  const [paletteAssignments, setPaletteAssignments] = useState<string[]>([])
  // The staffing targets and the team's shift, which the overstaffed check reads.
  const [targets, setTargets] = useState<StaffingTarget[]>([])
  const [shiftName, setShiftName] = useState('')
  const [overstaffedOpen, setOverstaffedOpen] = useState(false)
  // The team's own shift hours, as a board label (0700–1500).
  const [shiftTime, setShiftTime] = useState<string | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const [paletteNotice, setPaletteNotice] = useState<string | null>(null)
  const [leaveRequests, setLeaveRequests] = useState<TeamLeaveRequest[]>([])
  // What each agent has said about when they can work, read against the
  // schedule as an advisory flag (availabilityOverlay.ts).
  const [availability, setAvailability] = useState<TeamAvailability>({ rules: [], exceptions: [] })
  const [approvedScheduleByCell, setApprovedScheduleByCell] = useState<Map<string, ApprovedScheduleDay>>(new Map())
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  const currentRows = boardRoster.current
  const nextRows = boardRoster.next
  const currentTeamName = teams.find((team) => team.id === currentTeamId)?.name ?? ''
  const rows = rowsFor(period, currentRows, nextRows)
  const dates = datesFor(period)
  const periodMeta = period === 'current' ? PAY_PERIOD : PAY_PERIOD_20

  // Keyed by employee and ISO date, not period, so it resolves correctly
  // for pendingChanges below even though that loop covers overrides from
  // both periods at once while `period` itself only ever names one.
  const approvedLeaveByCell = approvedLeaveByCellFor(leaveRequests)
  const publishedSnapshotByCell = publishedSnapshotByCellFor(publishedVersions)

  // Every cell in `overrides` that does not match what was last saved
  // (or has no saved entry at all), across both periods at once: the same
  // scope the draft save itself covers. Doubles as pendingChanges.length > 0
  // for hasUnsavedChanges below, so an undo that lands back on the saved
  // state clears the unsaved warning the same way a fresh save does.
  const pendingChanges: {
    key: string
    period: Period
    employeeName: string
    date: BoardDate
    fromLabel: string
    fromDetail?: string
    toLabel: string
    toDetail?: string
  }[] = []
  for (const [key, override] of overrides) {
    const saved = baselineOverrides.get(key)
    if (saved && saved.category === override.category && saved.label === override.label && saved.detail === override.detail) {
      continue
    }
    const cellPeriod = key.split('|')[0] as Period
    const { employeeId, col } = parseCellKey(key)
    const member = rowsFor(cellPeriod, currentRows, nextRows).find((candidate) => candidate.id === employeeId)
    const date = datesFor(cellPeriod)[col]
    if (!member || !date) continue
    const base = member.days[col]
    // Absent a saved override, the prior value is whatever the cell
    // actually showed before this edit, which is the approved leave, the
    // approved schedule submission, or the team's own last published
    // version when one applies to it, not the schedule day it sits on
    // top of: the same precedence entryFor itself gives appliedLeave,
    // appliedSchedule, and publishedSnapshotByCell over member.days.
    const appliedLeave = approvedLeaveByCell.get(`${member.id}|${date.date}`)
    const appliedFullDayLeave = appliedLeave?.fullDay ? appliedLeave : undefined
    const appliedSchedule = approvedScheduleByCell.get(`${member.id}|${date.date}`)
    const publishedSnapshot = publishedSnapshotByCell.get(`${member.id}|${date.date}`)
    const priorLabel = saved
      ? saved.label
      : appliedFullDayLeave
        ? appliedFullDayLeave.label
        : appliedSchedule
          ? appliedSchedule.label
          : publishedSnapshot
            ? publishedSnapshot.label
            : (base?.label ?? 'Unsubmitted')
    const priorDetail = saved
      ? saved.detail
      : appliedFullDayLeave
        ? 'Full day'
        : appliedSchedule
          ? appliedSchedule.detail
          : publishedSnapshot
            ? publishedSnapshot.detail
            : base?.detail
    pendingChanges.push({
      key,
      period: cellPeriod,
      employeeName: member.name,
      date,
      fromLabel: priorLabel,
      fromDetail: priorDetail,
      toLabel: override.label,
      toDetail: override.detail,
    })
  }
  pendingChanges.sort((a, b) => a.date.date.localeCompare(b.date.date) || a.employeeName.localeCompare(b.employeeName))

  // True whenever the working overrides differ from what is actually
  // persisted: overrides only ever live in this component's own state
  // until the draft save (or Publish, which saves too) lands, so navigating
  // away in between would silently discard them, per
  // docs/design/style-guide.md's "protect unsaved work... with a warning
  // before navigation discards it."
  const hasUnsavedChanges = !sameOverrides(overrides, savedOverrides)
  // The latest working overrides, for the save that runs as the page closes.
  const overridesRef = useRef(overrides)
  overridesRef.current = overrides
  const unsavedRef = useRef(false)
  unsavedRef.current = hasUnsavedChanges

  // Covers the case in-app navigation cannot: closing the tab, refreshing,
  // or typing a new address. The Dashboard link below handles leaving to
  // another route within the app, which never fires beforeunload at all.
  useEffect(() => {
    if (!hasUnsavedChanges) return
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      // The draft is saved as the page closes; only a save that fails warns.
      if (!flushDraft()) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges, userId, currentTeamId])

  // Leaving the board with edits still waiting saves them first.
  useEffect(
    () => () => {
      if (unsavedRef.current) flushDraft()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, currentTeamId],
  )

  // Loads everything scoped to one team: the working draft and cell
  // history, keyed by team as well as by user
  // (planningBoardDraftStore.ts, planningBoardHistoryStore.ts), and the
  // team's own published versions, keyed by team in
  // muster.schedule_versions itself, so switching teams never mixes one
  // team's state into another's. Shared by the initial session effect
  // below and switchTeam further down, so the two can never load a
  // team's data differently from each other; both await it before
  // computing their own initial focus, even though a published snapshot
  // never actually changes which cell that lands on (a withdrawal still
  // empties a row regardless of publish history, this file's own
  // cellEditablePredicate comment), simply so neither one computes that
  // focus against published versions this call is still in the middle of
  // replacing.
  //
  // Every draft and history entry is filtered to the current key format
  // first (keepCurrentKeyFormat): a tab still open from before cellKey
  // changed to name-and-column can hold the old row-indexed format, and
  // comparing one of those against a freshly computed current-format key
  // (publicationStatusFor's own overridesMatch, notably) would never
  // match, reporting a published period Amended when nothing actually
  // changed. A published version's own overrides never need this: they
  // are always built fresh from muster.schedule_versions.
  async function loadTeamData(id: string, teamId: string, teamName: string): Promise<BoardRoster> {
    const saved = keepCurrentKeyFormat(getBoardDraft(id, teamId))
    const loaded = new Map(
      saved.map((entry): [string, Override] => [
        entry.key,
        { category: entry.category, label: entry.label, detail: entry.detail },
      ]),
    )
    setOverrides(loaded)
    setSavedOverrides(new Map(loaded))
    setBaselineOverrides(new Map(loaded))
    setBoardHistory(keepCurrentKeyFormat(getBoardHistory(id, teamId)))

    const [current, next, loadedRoster, catalog, locked, loadedTargets] = await Promise.all([
      getPublications(teamName, 'current', CURRENT_DATES[0]?.date ?? '', CURRENT_DATES),
      getPublications(teamName, 'next', PAY_PERIOD_20_DATES[0]?.date ?? '', PAY_PERIOD_20_DATES),
      getBoardRoster(teamId, CURRENT_DATES, PAY_PERIOD_20_DATES),
      getTargetCatalog(teamId),
      getPublishedPeriods(teamId),
      getStaffingTargets(),
    ])
    setTargets(loadedTargets)
    setShiftName(catalog.shift?.name ?? '')
    setLockedPeriods({ current: locked.has(CURRENT_DATES[0]?.date ?? ''), next: locked.has(PAY_PERIOD_20_DATES[0]?.date ?? '') })
    setPaletteAssignments(catalog.assignments.map((assignment) => assignment.name))
    setShiftTime(catalog.shift?.time ?? null)
    const loadedVersions: Record<Period, PublicationVersion[]> = { current, next }
    setPublishedVersions(loadedVersions)
    setBoardRoster(loadedRoster)

    setUndoStack([])
    setSelected(new Set())
    setAnchor(null)
    setCodeInput('')
    setReviewOpen(false)
    setHistoryOpenFor(null)
    setSavedAt(null)
    setSaveFailed(false)
    setPublishFailed(false)
    setPublishConfirmation(null)
    return loadedRoster
  }

  // The board needs userId before it can load or save a draft, so it waits
  // for the session to resolve before rendering interactive, the same race
  // AgentRequestLeavePage.tsx's own sessionReady guards against. Waiting on
  // the profile fetch too, rather than resolving sessionReady as soon as
  // the session itself does, closes the same race for supervisorName
  // (SupervisorApprovalInboxPage.tsx's own pattern): by the time the board
  // is interactive, Apply a status or code already has a real identity to
  // attribute a history entry to, and Publish already has one to record.
  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        const id = session?.user.id
        if (id) {
          setUserId(id)
          // The account's own real teams, never a fixed pair: the
          // persisted selection wins when it is still one of them, the
          // first team otherwise. An account that supervises none renders
          // no roster at all rather than another account's.
          const ownTeams = await getSupervisorTeams(id)
          setTeams(ownTeams)
          const persistedTeamId = getCurrentTeamId(id)
          const team = ownTeams.find((candidate) => candidate.id === persistedTeamId) ?? ownTeams[0]
          if (team) {
            setCurrentTeamIdState(team.id)
            const [leaveRequestsLoaded, approvedScheduleByCellLoaded, availabilityLoaded] = await Promise.all([
              getTeamLeaveRequests(),
              getApprovedScheduleDays(),
              schedulingServiceEnabled() ? getTeamAvailability(CURRENT_DATES[0]?.date ?? '') : Promise.resolve<TeamAvailability>({ rules: [], exceptions: [] }),
            ])
            setLeaveRequests(leaveRequestsLoaded)
            setAvailability(availabilityLoaded)
            setApprovedScheduleByCell(approvedScheduleByCellLoaded)
            // Purge before loadTeamData, not after: an override that
            // predates entryFor's own leave precedence
            // (SupervisorApprovalInboxPage.tsx's decide only catches a
            // leave newly approved this session, not one already on
            // record) has to be gone from storage before loadTeamData's
            // own getBoardDraft reads it, or it loads the stale entry
            // into overrides, ready for the draft save to write straight
            // back to storage.
            migrateOverridesForApprovedLeave(id, team.id, fullDayOnly(approvedLeaveByCellFor(leaveRequestsLoaded)))
            const loadedRoster = await loadTeamData(id, team.id, team.name)
            // The board always opens on the current period (the initial
            // Period state), and focused otherwise starts at row 0; if
            // that first employee has not submitted the period, row 0 is
            // disabled and the only tabIndex={0} cell in the whole table,
            // stranding a keyboard user who tabs into it. Move focus to
            // whichever cell actually is editable once the roster, and
            // any approved leave or approved schedule submission, are in.
            setFocused(
              firstEditableCellAt(
                loadedRoster.current,
                CURRENT_DATES,
                approvedLeaveByCellFor(leaveRequestsLoaded),
                approvedScheduleByCellLoaded,
              ),
            )
          }
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', id)
            .single()
          if (!cancelled && profile) {
            setSupervisorName(`${profile.first_name} ${profile.last_name}`)
          }
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // The sticky action bar only renders while selected.size > 0, so clearing
  // the selection from inside it (applying a category or code, or its own
  // Clear selection button) unmounts whatever button the keyboard user just
  // activated. The browser drops focus to <body> when that happens; restore
  // it to the cell the board itself still considers focused rather than
  // leaving a keyboard user stranded at the top of the page. Guarded on
  // activeElement being body specifically, so this never fights a user who
  // tabbed elsewhere on their own (switchPeriod's own selection reset, say).
  const previouslyHadSelection = useRef(false)
  useEffect(() => {
    const hasSelection = selected.size > 0
    const focusedId = rows[focused.row]?.id
    if (previouslyHadSelection.current && !hasSelection && document.activeElement === document.body && focusedId) {
      buttonRefs.current.get(cellKey(period, focusedId, focused.col))?.focus()
    }
    previouslyHadSelection.current = hasSelection
  }, [selected, focused, period, rows])

  // Widened with whatever assignment a supervisor has applied this
  // session (a code like Administrative duty or Court, none of which the
  // roster itself ever carries), so the filter can find a cell the board
  // itself just created rather than only ones the fixture data shipped
  // with.
  const assignmentOptions = Array.from(
    new Set([
      ...workAssignmentsOf([...currentRows, ...nextRows]),
      ...[...overrides.values()].filter((override) => override.category === 'work' && override.detail).map(
        (override) => override.detail as string,
      ),
    ]),
  ).sort()

  const periodLocked = lockedPeriods[period]
  const isCellSubmitted = cellEditablePredicate(rows, dates, approvedLeaveByCell, approvedScheduleByCell)
  // A published period is read-only: nothing on it can be selected or changed
  // until the supervisor reopens it.
  const isCellEditable = (row: number, col: number) => !periodLocked && isCellSubmitted(row, col)

  // Returns whether the save landed, the same as planningBoardDraftStore.ts's
  // own saveBoardDraft: publish() below needs to know before it reports a
  // version as published while the draft behind it silently failed to save.
  function saveDraft(options: { auto?: boolean } = {}): boolean {
    if (!userId) {
      setSaveFailed(true)
      return false
    }
    const entries: BoardOverrideEntry[] = [...overrides.entries()].map(([key, override]) => ({ key, ...override }))
    if (!saveBoardDraft(userId, currentTeamId, entries)) {
      setSaveFailed(true)
      return false
    }
    setSaveFailed(false)
    setSavedAt(new Date())
    setSavedOverrides(new Map(overrides))
    // An automatic save keeps the undo history and the review baseline, since
    // the supervisor is still working; a publish starts both afresh.
    if (!options.auto) {
      setUndoStack([])
      setBaselineOverrides(new Map(overrides))
    }
    return true
  }

  // Writes the latest edits straight away, without waiting for the pause and
  // without touching state, for a page that is closing or being left.
  function flushDraft(): boolean {
    if (!userId) return false
    const entries: BoardOverrideEntry[] = [...overridesRef.current.entries()].map(([key, override]) => ({ key, ...override }))
    return saveBoardDraft(userId, currentTeamId, entries)
  }

  // The draft saves itself a moment after the last edit.
  useEffect(() => {
    // Not gated on the lock: the draft holds both periods, so an edit made
    // before switching to a published one still has to be saved.
    if (!hasUnsavedChanges || !userId) return
    const timer = window.setTimeout(() => saveDraft({ auto: true }), AUTOSAVE_DELAY_MS)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrides, hasUnsavedChanges, userId])

  function entryFor(row: number, col: number): DayEntry | null {
    const member = rows[row]
    const date = dates[col]
    const cellKeyName = `${member.id}|${date.date}`
    // Only a full-day approved leave replaces the cell's own category:
    // a partial-day one still leaves the employee on duty for the rest
    // of the day, so it must not read as an ordinary full-day leave cell
    // and drop them from the daily assignment board's own on-duty count
    // the way a real full-day leave correctly does.
    const appliedLeave = approvedLeaveByCell.get(cellKeyName)
    const appliedFullDayLeave = appliedLeave?.fullDay ? appliedLeave : undefined
    const appliedSchedule = approvedScheduleByCell.get(cellKeyName)
    const publishedSnapshot = publishedSnapshotByCell.get(cellKeyName)

    // Checked before the override below, not after: a Pay Period 20
    // submission a supervisor has since returned or denied is withdrawn
    // (nextRowsFor above), and a draft edit made while it was still
    // editable should not go on showing as part of the staffing picture
    // once it no longer is. An approved-leave or approved-schedule cell
    // is exempt from this gate the same way cellEditablePredicate exempts
    // it from row-level editability, so its own override below can still
    // be read. A published snapshot is deliberately not exempt: a
    // withdrawal has to actually empty the row, even one that was part of
    // an earlier publish, or the period would keep reading Published
    // instead of flagging the now-stale version as Amended
    // (publicationStatusFor below), which is the one signal that tells a
    // supervisor to republish.
    if (!member.editable && !appliedFullDayLeave && !appliedSchedule) return null

    // An override always wins once its cell is editable, the same as it
    // already does over an ordinary scheduled day below: a supervisor who
    // applies Work, Off, or another code to an approved-leave,
    // approved-schedule, or published cell (the leave was recalled, the
    // schedule covered another way, or the published version is being
    // amended) needs that choice to actually show and publish, not sit
    // invisibly under the label it was applied to replace. Leave wins
    // over an approved schedule, which wins over a published snapshot,
    // when more than one applies to the same cell, the same priority
    // order the fallbacks below already read in.
    const override = overrides.get(cellKey(period, member.id, col))
    if (override) {
      const base = appliedFullDayLeave
        ? { ...date, category: 'leave' as Category, label: appliedFullDayLeave.label, detail: 'Full day' }
        : appliedSchedule
          ? { ...date, ...appliedSchedule }
          : publishedSnapshot
            ? { ...date, ...publishedSnapshot }
            : member.days[col]
      return { ...base, category: override.category, label: override.label, detail: override.detail, state: undefined }
    }

    if (appliedFullDayLeave) {
      return { ...date, category: 'leave', label: appliedFullDayLeave.label, detail: 'Full day', state: 'approved' }
    }

    if (appliedSchedule) {
      return { ...date, ...appliedSchedule, state: 'approved' }
    }

    if (publishedSnapshot) {
      return { ...date, ...publishedSnapshot, state: 'published' }
    }

    return member.days[col]
  }

  const hasActiveFilters = statusFilters.size > 0 || assignmentFilter !== null

  // An unsubmitted cell (no entry) never matches: with any filter active it
  // dims along with every other cell that does not match.
  function matchesFilters(entry: DayEntry | null): boolean {
    if (!entry) return false
    if (statusFilters.size > 0 && !statusFilters.has(entry.category)) return false
    if (assignmentFilter && entry.detail !== assignmentFilter) return false
    return true
  }

  function toggleStatusFilter(category: Category) {
    setStatusFilters((current) => {
      const next = new Set(current)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  function clearFilters() {
    setStatusFilters(new Set())
    setAssignmentFilter(null)
  }

  function switchPeriod(next: Period) {
    setPeriod(next)
    setSelected(new Set())
    setAnchor(null)
    setCodeInput('')
    const rowsForNext = rowsFor(next, currentRows, nextRows)
    setFocused(firstEditableCellAt(rowsForNext, datesFor(next), approvedLeaveByCell, approvedScheduleByCell))
  }

  // Every team a supervisor owns keeps its own draft, publications, and
  // cell history (loadTeamData above), so switching teams is a fresh load
  // rather than a filter over already-loaded state; asks first if the
  // team being left has unsaved changes, the same guard the Dashboard
  // link below uses.
  async function switchTeam(nextTeamId: string) {
    if (nextTeamId === currentTeamId) return
    if (hasUnsavedChanges && !flushDraft() && !window.confirm('Switch teams? Your latest changes could not be saved and will be lost.')) {
      return
    }
    const nextTeam = teams.find((team) => team.id === nextTeamId)
    if (!userId || !nextTeam) return
    setCurrentTeamIdState(nextTeamId)
    persistCurrentTeamId(userId, nextTeamId)
    // Same ordering as the mount effect: the destination team's draft can
    // hold an override that predates entryFor's own leave precedence, and
    // loadTeamData's own getBoardDraft has to see it already purged.
    migrateOverridesForApprovedLeave(userId, nextTeamId, fullDayOnly(approvedLeaveByCell))
    const loadedRoster = await loadTeamData(userId, nextTeamId, nextTeam.name)
    setPeriod('current')
    setFocused(firstEditableCellAt(loadedRoster.current, CURRENT_DATES, approvedLeaveByCell, approvedScheduleByCell))
  }

  // toggle (Ctrl/Cmd) adds or removes exactly this cell from whatever is
  // already selected, independent of extend (Shift)'s own rectangle: a
  // supervisor applying one status to, say, every Friday across several
  // employees needs cells that share no row or column in common, which a
  // range between two corners can never express. Takes priority over
  // extend when both are held, the same as a spreadsheet's own Ctrl+Shift
  // click leaves ambiguous otherwise. Toggling moves the anchor to this
  // cell, so a Shift+click right after starts its own new range from here
  // rather than from wherever the selection last happened to anchor.
  function activate(cell: Cell, extend: boolean, toggle: boolean) {
    if (!isCellEditable(cell.row, cell.col)) return
    const key = cellKey(period, rows[cell.row].id, cell.col)
    if (toggle) {
      setSelected((current) => {
        const next = new Set(current)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
      setAnchor(cell)
      setCodeInput('')
    } else if (extend && anchor) {
      setSelected(rectangleKeys(period, anchor, cell, rows, isCellEditable))
    } else {
      const isSoleSelection = selected.size === 1 && selected.has(key)
      setSelected(isSoleSelection ? new Set() : new Set([key]))
      setAnchor(isSoleSelection ? null : cell)
      setCodeInput('')
    }
    setFocused(cell)
  }

  // A withdrawn or otherwise unsubmitted row can fall anywhere in the
  // roster now, not only its top or bottom, so a vertical move routes
  // around one via nextEditableRow instead of just refusing to land on it:
  // stopping dead the moment one is adjacent would strand every row past
  // it, reachable only with a pointer. isCellEditable, not just the row's
  // own editable flag, is what nextEditableRow checks here: an approved
  // leave can make one cell in an otherwise unsubmitted row landable even
  // though the rest of that row still is not.
  function moveFocus(cell: Cell, extend: boolean) {
    const rowDirection = cell.row > focused.row ? 1 : cell.row < focused.row ? -1 : 0
    const targetCol = Math.min(Math.max(cell.col, 0), dates.length - 1)
    const clamped: Cell = {
      row: nextEditableRow(rows.length, focused.row, rowDirection, (row) => isCellEditable(row, targetCol)),
      col: targetCol,
    }
    if (!isCellEditable(clamped.row, clamped.col)) return
    if (extend) {
      const from = anchor ?? focused
      if (!anchor) setAnchor(focused)
      setSelected(rectangleKeys(period, from, clamped, rows, isCellEditable))
    }
    setFocused(clamped)
    buttonRefs.current.get(cellKey(period, rows[clamped.row].id, clamped.col))?.focus()
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, cell: Cell) {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        moveFocus({ row: cell.row, col: cell.col + 1 }, event.shiftKey)
        break
      case 'ArrowLeft':
        event.preventDefault()
        moveFocus({ row: cell.row, col: cell.col - 1 }, event.shiftKey)
        break
      case 'ArrowDown':
        event.preventDefault()
        moveFocus({ row: cell.row + 1, col: cell.col }, event.shiftKey)
        break
      case 'ArrowUp':
        event.preventDefault()
        moveFocus({ row: cell.row - 1, col: cell.col }, event.shiftKey)
        break
      case ' ':
        event.preventDefault()
        activate(cell, event.shiftKey, event.ctrlKey || event.metaKey)
        break
      default:
        break
    }
  }

  // Work is the one category docs/product/scheduling-model.md's schedule
  // entry requires an assignment for; the other five quick-select
  // categories carry none, so there is nothing of theirs to preserve. A
  // cell that was already a work entry keeps its own real assignment;
  // anything else (a day off, training, leave, no entry at all) has none
  // to carry forward, since its own detail means something else entirely
  // there, so it falls back to this employee's own most common real
  // assignment instead (defaultAssignmentFor).
  function workAssignmentFor(employeeId: string, col: number): string | undefined {
    const row = rows.findIndex((member) => member.id === employeeId)
    const current = row >= 0 ? entryFor(row, col) : null
    if (current?.category === 'work' && current.detail) return current.detail
    return defaultAssignmentFor(employeeId, currentRows, nextRows)
  }

  // Pushes the pre-change snapshot onto the undo stack (capped at 50 so it
  // cannot grow without bound across a long editing session), applies
  // `next`, and records a history entry per cell `changedKeys` names. The
  // one place applyCategory and applyCode actually commit a change, so
  // undo and cell history can never drift out of sync with the overrides
  // that produced them.
  function commitOverrides(next: Map<string, Override>, changedKeys: string[]) {
    setUndoStack((stack) => [...stack.slice(-49), new Map(overrides)])
    setOverrides(next)
    const changedBy = supervisorName ?? 'Unknown supervisor'
    const changedAt = new Date().toISOString()
    const entries: BoardHistoryEntry[] = changedKeys.map((key) => {
      const override = next.get(key) as Override
      return { key, category: override.category, label: override.label, detail: override.detail, changedBy, changedAt }
    })
    setBoardHistory((current) => [...current, ...entries])
    if (userId) appendBoardHistory(userId, currentTeamId, entries)
    setSelected(new Set())
    setAnchor(null)
    setCodeInput('')
    setSavedAt(null)
    setPublishConfirmation(null)
  }

  function applyCategory(option: (typeof CATEGORY_OPTIONS)[number]) {
    const next = new Map(overrides)
    const changedKeys: string[] = []
    for (const key of selected) {
      const { employeeId, col } = parseCellKey(key)
      const detail = option.key === 'work' ? workAssignmentFor(employeeId, col) : undefined
      next.set(key, { category: option.key, label: option.patchLabel, detail })
      changedKeys.push(key)
    }
    commitOverrides(next, changedKeys)
  }

  // Sets days to work on a given assignment: a day already on work keeps its own
  // hours, and any other day takes the team's shift hours. Only days that can be edited
  // change, so an approved leave day or an unsubmitted one is left as it is.
  function applyAssignment(assignmentName: string, keys: string[]) {
    const targets = keys.flatMap((key) => {
      const { employeeId, col } = parseCellKey(key)
      const row = rows.findIndex((member) => member.id === employeeId)
      return row >= 0 && isCellEditable(row, col) ? [{ key, row, col }] : []
    })
    if (targets.length === 0) {
      setPaletteNotice(`${assignmentName} was not applied: none of those days can be edited.`)
      return
    }
    const next = new Map(overrides)
    for (const { key, row, col } of targets) {
      const current = entryFor(row, col)
      next.set(key, { category: 'work', label: current?.category === 'work' ? current.label : (shiftTime ?? SHIFT_TEMPLATE.time), detail: assignmentName })
    }
    commitOverrides(next, targets.map((target) => target.key))
    setPaletteNotice(`${assignmentName} applied to ${targets.length} ${targets.length === 1 ? 'day' : 'days'}.`)
  }

  function pressAssignment(assignmentName: string) {
    if (selected.size === 0) {
      setPaletteNotice(`Select one or more days first, then press ${assignmentName}. Or drag it onto a day.`)
      return
    }
    applyAssignment(assignmentName, [...selected])
  }

  function dropAssignment(event: React.DragEvent<HTMLElement>, key: string) {
    event.preventDefault()
    const assignmentName = dragging ?? event.dataTransfer.getData('text/plain')
    setDragging(null)
    setDragOverKey(null)
    if (!assignmentName) return
    // Dropping on a selected day changes every selected day; on any other day, only that one.
    applyAssignment(assignmentName, selected.has(key) ? [...selected] : [key])
  }

  const matchedCode = BOARD_CODES.find((code) => code.code.toLowerCase() === codeInput.trim().toLowerCase())

  function applyCode() {
    if (!matchedCode) return
    const next = new Map(overrides)
    const changedKeys: string[] = []
    for (const key of selected) {
      // "Scheduled workday" is the one work code with no assignment of
      // its own (docs/product/scheduling-model.md's catalog); every
      // other code, work or not, keeps its own explicit detail exactly
      // as before.
      const { employeeId, col } = parseCellKey(key)
      const detail =
        matchedCode.category === 'work' && !matchedCode.detail ? workAssignmentFor(employeeId, col) : matchedCode.detail
      next.set(key, { category: matchedCode.category, label: matchedCode.label, detail })
      changedKeys.push(key)
    }
    commitOverrides(next, changedKeys)
  }

  // Restores the overrides map to just before the last Apply a status or
  // Apply code action. Cleared the moment the board is saved or published
  // (saveDraft empties undoStack), so there is nothing to undo back past
  // that point, the same boundary the daily assignment board's own Undo
  // describes for itself.
  function undo() {
    if (undoStack.length === 0) return
    const previous = undoStack[undoStack.length - 1]
    setOverrides(previous)
    setUndoStack((stack) => stack.slice(0, -1))
  }

  // What a period's board actually renders, cell by cell, rather than just
  // its own explicit overrides: entryFor is already the one place that
  // resolves an approved leave ahead of an override, an override ahead of
  // the base roster, and a withdrawn submission down to nothing
  // (member.editable), so reading every cell through it, not just the
  // ones overrides or approvedLeaveByCell happen to name, is what keeps a
  // version snapshot honest about a board a supervisor never touched a
  // single override on, and about a submission returned or denied after
  // publication, which changes nothing in overrides but still empties a
  // whole row. A cell entryFor returns null for (withdrawn, no leave) is
  // left out of the list entirely, so its own disappearance or
  // reappearance still changes the list's length and flips the
  // comparison, the same as any other visible change would. rows and
  // dates are always the ones targetPeriod's own toggle already switched
  // the board to, since publish and publicationStatusFor are only ever
  // called for the period on screen.
  function effectivePeriodEntries(targetPeriod: Period): BoardOverrideEntry[] {
    const entries: BoardOverrideEntry[] = []
    rows.forEach((member, row) => {
      dates.forEach((_date, col) => {
        const entry = entryFor(row, col)
        // A day with nothing scheduled is not an override or an entry: leaving it
        // out keeps a published version and the board reading the same after a reload.
        if (entry && entry.category !== 'unscheduled') {
          entries.push({ key: cellKey(targetPeriod, member.id, col), category: entry.category, label: entry.label, detail: entry.detail })
        }
      })
    })
    return entries
  }

  // Lifts the lock on a published period: the board can be edited again and
  // the team's agents can submit into it again, until it is published once more.
  async function reopen(targetPeriod: Period) {
    const label = targetPeriod === 'current' ? PAY_PERIOD.label : PAY_PERIOD_20.label
    if (!window.confirm(`Reopen ${label}? The team's agents can submit into it again, and you can edit the board until you publish it again.`)) return
    const result = await reopenPayPeriod(currentTeamName, datesFor(targetPeriod)[0].date)
    if (!result.ok) {
      setPublishMessage(result.message)
      setPublishFailed(true)
      return
    }
    setPublishFailed(false)
    setPublishMessage(null)
    setPublishConfirmation(null)
    setLockedPeriods((current) => ({ ...current, [targetPeriod]: false }))
  }

  async function publish(targetPeriod: Period) {
    if (!userId || !supervisorName) {
      setPublishFailed(true)
      return
    }
    const targetDates = datesFor(targetPeriod)
    const periodEntries = effectivePeriodEntries(targetPeriod)
    // Re-keyed from the board's own cellKey (period|id|column) to
    // employeeId+date: a published snapshot has to outlive the
    // specific column layout that produced it (publishedScheduleStore.ts's
    // own header comment).
    const targetRows = rowsFor(targetPeriod, currentRows, nextRows)
    const wireEntries = wireEntriesFor(periodEntries, targetDates, targetRows)
    const published = await publishBoard(
      currentTeamName,
      targetDates[0].date,
      targetDates[targetDates.length - 1].date,
      wireEntries,
    )
    if (!published.ok) {
      setPublishMessage(published.message)
      setPublishFailed(true)
      return
    }
    const version: PublicationVersion = { ...published.meta, overrides: periodEntries }
    setPublishFailed(false)
    setPublishMessage(null)
    setLockedPeriods((current) => ({ ...current, [targetPeriod]: true }))
    setSelected(new Set())
    setAnchor(null)
    setPublishedVersions((current) => ({ ...current, [targetPeriod]: [...current[targetPeriod], version] }))
    setPublishConfirmation({ period: targetPeriod, version: version.version, at: new Date() })
    // Publish also saves the working draft, so the two never disagree the
    // moment it runs: a version is a claim about what the board currently
    // shows, and that claim should hold even for cells outside the
    // published period.
    saveDraft()
  }

  // Draft before any publish, Published immediately after, Amended once a
  // further edit makes the period's current overrides differ from its
  // latest version again. docs/design/style-guide.md's status vocabulary.
  function publicationStatusFor(targetPeriod: Period): { state: RecordState; version?: number } {
    const versions = publishedVersions[targetPeriod]
    if (versions.length === 0) return { state: 'draft' }
    const latest = versions[versions.length - 1]
    const currentEntries = effectivePeriodEntries(targetPeriod)
    const matches = overridesMatch(currentEntries, latest.overrides)
    // Published while it still reads as the last version. Once it differs (an
    // approved leave request, a withdrawn submission, an edit after reopening)
    // it is Amended; a reopened period that has not changed is a draft.
    if (lockedPeriods[targetPeriod]) return { state: matches ? 'published' : 'amended', version: latest.version }
    return { state: matches ? 'draft' : 'amended', version: latest.version }
  }

  if (!sessionReady) {
    return (
      <div className="flex h-dvh flex-col bg-surface text-ink">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  const boardStatus = publicationStatusFor(period)

  // An assignment with more people on it than its staffing target plus the
  // overstaffed margin, per day, read from the board as it stands (overrides
  // and drafts included). Only an assignment with a target is judged.
  const overstaffed: { col: number; assignment: string; finding: CoverageFinding }[] = []
  dates.forEach((date, col) => {
    const people = rows.flatMap((member, row) => {
      const entry = entryFor(row, col)
      return entry ? [{ id: member.id, category: entry.category, detail: entry.detail, countsAsAvailable: entry.countsAsAvailable }] : []
    })
    for (const finding of coverageFor(people, targets, { date: date.date, team: currentTeamName, shift: shiftName })) {
      if (finding.state === 'overstaffed' && finding.assignment !== WHOLE_SHIFT) overstaffed.push({ col, assignment: finding.assignment, finding })
    }
  })
  const overstaffedCells = new Set(overstaffed.map((item) => `${item.col}|${item.assignment}`))

  // A work entry that runs into time its agent said they cannot work, for the
  // period on screen. Advisory: it marks the cell and lists it, and never
  // stops an edit or a publish.
  function availabilityConflictAt(row: number, col: number, entry: DayEntry): AvailabilityConflict | null {
    if (entry.category !== 'work') return null
    return workConflictFor(rows[row].id, dates[col].date, workWindowOf(entry.label), availability.rules, availability.exceptions)
  }
  const availabilityConflicts = rows.flatMap((member, row) =>
    dates.flatMap((date, col) => {
      const entry = entryFor(row, col)
      const conflict = entry ? availabilityConflictAt(row, col, entry) : null
      return entry && conflict ? [{ key: `${member.id}|${col}`, employee: member.name, date, conflict }] : []
    }),
  )
  const availabilityConflictCells = new Set(availabilityConflicts.map((item) => item.key))
  const availabilityConflictsShown = availabilityConflicts.slice(0, 5)
  const overstaffedShown = overstaffedOpen ? overstaffed : overstaffed.slice(0, 3)
  const soleSelectedKey = selected.size === 1 ? [...selected][0] : null
  const cellHistory = soleSelectedKey ? historyForCell(boardHistory, soleSelectedKey) : []

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#planning-board-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="planning-board-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto p-4 sm:p-6 lg:p-8">
        <SupervisorNav
          beforeLeave={() => {
            // React Router's own navigation never fires the browser's beforeunload
            // event, so the guard above never sees this leave; ask the same
            // question here instead.
            return !hasUnsavedChanges || flushDraft() || window.confirm('Leave? Your latest changes could not be saved and will be lost.')
          }}
        />
        <div className="mx-auto flex w-full max-w-[1400px] flex-1 flex-col gap-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-h2 font-extrabold">Planning Board</h1>
                <StatusChip state={boardStatus.state} />
                {boardStatus.version && <span className="text-caption text-ink-subtle">v{boardStatus.version}</span>}
                <button
                  type="button"
                  aria-label="Selection controls"
                  aria-expanded={selectionHelpOpen}
                  onClick={() => setSelectionHelpOpen((open) => !open)}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-ink-muted hover:bg-surface-2 hover:text-ink"
                >
                  <InfoIcon />
                </button>
              </div>
              <p className="mt-1 text-small text-ink-muted">
                {currentTeamName} &middot; {periodMeta.label}, {periodMeta.range}.{' '}
                {periodLocked
                  ? 'Published, so it cannot be changed. Reopen it to edit the board; the team\'s agents can then submit into it again.'
                  : 'Select one or several cells, then apply a status to all of them.'}
              </p>
              {selectionHelpOpen && (
                <p role="note" className="mt-1 max-w-prose text-small text-ink-muted">
                  Click a cell to select it. Shift+click selects a rectangular range. Ctrl+click (Cmd+click on Mac)
                  adds or removes individual cells from the selection.
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={undo}
                  disabled={undoStack.length === 0 || periodLocked}
                  className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:pointer-events-none disabled:opacity-40"
                >
                  Undo
                </button>
                {pendingChanges.length > 0 && (
                  <button
                    type="button"
                    aria-expanded={reviewOpen}
                    onClick={() => setReviewOpen((open) => !open)}
                    className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                  >
                    Review changes ({pendingChanges.length})
                  </button>
                )}
                {periodLocked ? (
                  <button
                    type="button"
                    onClick={() => void reopen(period)}
                    className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
                  >
                    Reopen {periodMeta.label}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => publish(period)}
                    className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
                  >
                    Publish {periodMeta.label}
                  </button>
                )}
              </div>
              {publishFailed && (
                <p role="alert" className="text-caption font-semibold text-error">
                  {publishMessage ? `Could not publish. ${publishMessage}.` : 'Could not publish. Storage may be unavailable in this browser.'}
                </p>
              )}
              {saveFailed ? (
                <p role="alert" className="text-caption font-semibold text-error">
                  Could not save. Storage may be unavailable in this browser.
                </p>
              ) : (
                (hasUnsavedChanges || savedAt) && (
                  <p role="status" className="text-caption text-ink-subtle">
                    {hasUnsavedChanges
                      ? 'Saving…'
                      : `Draft saved ${savedAt?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                  </p>
                )
              )}
              {publishConfirmation && publishConfirmation.period === period && (
                <p className="text-caption text-ink-subtle">
                  Published v{publishConfirmation.version}{' '}
                  {publishConfirmation.at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>

          {reviewOpen && pendingChanges.length > 0 && (
            <div role="region" aria-label="Pending changes" className="flex flex-col gap-2 rounded-surface border border-line bg-canvas p-3">
              <div className="flex items-center justify-between">
                <h2 className="text-small font-bold">Pending changes</h2>
                <button
                  type="button"
                  onClick={() => setReviewOpen(false)}
                  className="flex min-h-11 items-center justify-center rounded-control px-2 text-caption font-bold text-ink-muted hover:bg-surface-2"
                >
                  Close
                </button>
              </div>
              <ul className="flex max-h-64 flex-col gap-1.5 overflow-y-auto">
                {pendingChanges.map((change) => (
                  <li key={change.key} className="flex flex-wrap items-baseline gap-1.5 text-small">
                    <span className="font-bold">{change.employeeName}</span>
                    <span className="text-ink-muted">
                      {change.date.weekday} {change.date.month} {change.date.day}:
                    </span>
                    <span className="text-ink-subtle line-through">
                      {formatScheduleLabel(change.fromLabel, format)}
                      {change.fromDetail ? ` · ${change.fromDetail}` : ''}
                    </span>
                    <span aria-hidden="true">&rarr;</span>
                    <span className="font-semibold">
                      {formatScheduleLabel(change.toLabel, format)}
                      {change.toDetail ? ` · ${change.toDetail}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <TeamSwitcher teams={teams} currentTeamId={currentTeamId} onChange={switchTeam} />

          <div role="group" aria-label="Which pay period" className="flex gap-0.5 self-start rounded-control bg-surface-2 p-0.5">
            <button
              type="button"
              aria-pressed={period === 'current'}
              onClick={() => switchPeriod('current')}
              className={`flex min-h-11 items-center justify-center rounded-[5px] px-4 text-small font-bold ${
                period === 'current' ? 'bg-canvas text-ink shadow-sm' : 'text-ink-subtle'
              }`}
            >
              {PAY_PERIOD.label}
            </button>
            <button
              type="button"
              aria-pressed={period === 'next'}
              onClick={() => switchPeriod('next')}
              className={`flex min-h-11 items-center justify-center rounded-[5px] px-4 text-small font-bold ${
                period === 'next' ? 'bg-canvas text-ink shadow-sm' : 'text-ink-subtle'
              }`}
            >
              {PAY_PERIOD_20.label}
            </button>
          </div>

          {overstaffed.length > 0 && (
            <div role="status" className="rounded-surface border border-error bg-canvas p-3 text-small">
              <p className="font-bold text-error">
                {overstaffed.length === 1 ? 'An assignment is overstaffed' : `${overstaffed.length} assignments are overstaffed`}
              </p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {overstaffedShown.map((item) => (
                  <li key={`${item.col}|${item.assignment}`}>
                    {item.assignment} is overstaffed on {dates[item.col].weekday} {dates[item.col].month} {dates[item.col].day}: {item.finding.actual} assigned, target {item.finding.required}.
                  </li>
                ))}
              </ul>
              {overstaffed.length > 3 && (
                <button
                  type="button"
                  aria-expanded={overstaffedOpen}
                  onClick={() => setOverstaffedOpen((open) => !open)}
                  className="mt-1 flex min-h-11 items-center rounded-control px-1 text-small font-bold text-primary underline underline-offset-2"
                >
                  {overstaffedOpen ? 'Show fewer' : `Show all ${overstaffed.length}`}
                </button>
              )}
            </div>
          )}

          {availabilityConflicts.length > 0 && (
            <div role="status" className="rounded-surface border border-warning bg-canvas p-3 text-small">
              <p className="font-bold text-warning">
                {availabilityConflicts.length === 1
                  ? 'An assignment falls outside stated availability'
                  : `${availabilityConflicts.length} assignments fall outside stated availability`}
              </p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {availabilityConflictsShown.map((item) => (
                  <li key={item.key}>
                    {item.employee} is scheduled on {item.date.weekday} {item.date.month} {item.date.day} and marked{' '}
                    {item.conflict.overnight ? 'unavailable after midnight' : 'unavailable'}{' '}
                    {item.conflict.source === 'exception' ? 'by an approved exception' : item.conflict.overnight ? 'for the following weekday' : 'for that weekday'}.
                  </li>
                ))}
              </ul>
              {availabilityConflicts.length > availabilityConflictsShown.length && (
                <p className="mt-1 text-ink-muted">and {availabilityConflicts.length - availabilityConflictsShown.length} more.</p>
              )}
              <p className="mt-1 text-ink-muted">Availability is shown for your information; it does not stop a change or a publish.</p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
            <div role="group" aria-label="Filter by status" className="flex flex-wrap gap-2">
              {CATEGORY_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={statusFilters.has(option.key)}
                  onClick={() => toggleStatusFilter(option.key)}
                  className={`flex min-h-11 min-w-11 items-center justify-center rounded-control border px-3 text-caption font-bold ${
                    statusFilters.has(option.key) ? 'border-primary bg-selected' : 'border-line-strong hover:bg-surface-2'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="board-assignment-filter" className="text-caption font-bold text-ink-muted">
                Assignment
              </label>
              <select
                id="board-assignment-filter"
                value={assignmentFilter ?? ''}
                onChange={(event) => setAssignmentFilter(event.target.value === '' ? null : event.target.value)}
                className="min-h-11 rounded-control border border-line-strong px-2 text-caption"
              >
                <option value="">All assignments</option>
                {assignmentOptions.map((assignment) => (
                  <option key={assignment} value={assignment}>
                    {assignment}
                  </option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto flex min-h-11 min-w-11 items-center justify-center rounded-control px-3 text-caption font-bold text-ink-muted hover:bg-surface-2"
              >
                Clear filters
              </button>
            )}
          </div>

          {rows.length > 0 && paletteAssignments.length > 0 && !periodLocked && (
            <div role="group" aria-label="Assignments" className="sticky -top-4 z-20 flex flex-wrap items-center gap-2 rounded-surface border border-line bg-canvas p-3 sm:-top-6 lg:-top-8">
              <span className="text-caption font-bold text-ink-muted">Assignments</span>
              {paletteAssignments.map((name) => (
                <button
                  key={name}
                  type="button"
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', name)
                    event.dataTransfer.effectAllowed = 'copy'
                    setDragging(name)
                  }}
                  onDragEnd={() => {
                    setDragging(null)
                    setDragOverKey(null)
                  }}
                  onClick={() => pressAssignment(name)}
                  className="flex min-h-11 cursor-grab items-center rounded-control border border-line-strong bg-surface px-3 text-caption font-bold hover:bg-surface-2 active:cursor-grabbing"
                >
                  {name}
                </button>
              ))}
              <p className="basis-full text-caption text-ink-subtle">
                Drag an assignment onto a day, or select days and press one to apply it. Dropping on a selected day applies it to every selected day.
              </p>
              <p role="status" className="basis-full text-caption font-semibold text-ink-muted empty:hidden">
                {paletteNotice}
              </p>
            </div>
          )}

          <div
            role="region"
            aria-label="Planning board"
            tabIndex={0}
            className="overflow-x-auto rounded-surface border border-line"
          >
            {rows.length === 0 && <EmptyState>No employees are assigned to this team.</EmptyState>}
            <table className={rows.length === 0 ? 'hidden' : 'border-collapse'}>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 z-10 min-w-[160px] border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide text-ink-subtle uppercase"
                  >
                    Employee
                  </th>
                  {dates.map((date, col) => (
                    <th
                      key={date.date}
                      scope="col"
                      className={`min-w-[110px] border-b border-line bg-surface-2 px-2 py-2 text-center text-caption font-bold text-ink-subtle [font-variant-numeric:tabular-nums] ${
                        col === WEEK_1_LAST_COLUMN ? 'border-r-2 border-r-line-strong' : ''
                      }`}
                    >
                      {date.weekday} {date.day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((member, row) => (
                  <tr key={member.id}>
                    <th
                      scope="row"
                      className="sticky left-0 z-10 border-b border-line bg-canvas px-3 py-2 text-left text-small font-bold"
                    >
                      {member.name}
                    </th>
                    {dates.map((date, col) => {
                      const entry = entryFor(row, col)
                      const key = cellKey(period, member.id, col)
                      const isSelected = selected.has(key)
                      const isFocusable = focused.row === row && focused.col === col

                      if (!entry) {
                        return (
                          <td
                            key={key}
                            className={`border-b border-line bg-surface-2 p-0 align-middle ${
                              col === WEEK_1_LAST_COLUMN ? 'border-r-2 border-r-line-strong' : ''
                            }`}
                          >
                            <button
                              type="button"
                              disabled
                              aria-label={`${member.name}, ${date.weekday} ${date.month} ${date.day}, Unsubmitted`}
                              tabIndex={-1}
                              className={`flex min-h-14 w-full items-center justify-center px-2 py-1.5 text-center text-caption font-semibold text-ink-subtle italic ${
                                hasActiveFilters ? 'opacity-30' : ''
                              }`}
                            >
                              Unsubmitted
                            </button>
                          </td>
                        )
                      }

                      const isFilterMatch = hasActiveFilters && matchesFilters(entry)
                      const isOverstaffedCell = entry.category === 'work' && Boolean(entry.detail) && overstaffedCells.has(`${col}|${entry.detail}`)
                      const isOutsideAvailability = availabilityConflictCells.has(`${member.id}|${col}`)

                      return (
                        <td
                          key={key}
                          // The fill, the filter-match outline, and the
                          // selection ring all live here rather than on the
                          // button: a table cell's own height is always the
                          // row's real height, but a percentage height (or
                          // a box-shadow sized off it) on a child inside a
                          // td does not reliably resolve to that same
                          // height, so anything drawn on the button instead
                          // stops at its own shorter content height, well
                          // short of a taller neighboring row.
                          onDragOver={(event) => {
                            if (dragging && isCellEditable(row, col)) {
                              event.preventDefault()
                              setDragOverKey(key)
                            }
                          }}
                          onDragLeave={() => setDragOverKey((current) => (current === key ? null : current))}
                          onDrop={(event) => {
                            if (dragging && isCellEditable(row, col)) dropAssignment(event, key)
                          }}
                          className={`border-b border-line p-0 align-middle ${
                            col === WEEK_1_LAST_COLUMN ? 'border-r-2 border-r-line-strong' : ''
                          } ${isOverstaffedCell ? 'border-t-2 border-t-error bg-error/20' : CATEGORY_FILL[entry.category]} ${
                            isFilterMatch || dragOverKey === key ? 'outline outline-2 -outline-offset-2 outline-primary' : ''
                          } ${isSelected ? 'ring-2 ring-inset ring-primary' : ''}`}
                        >
                          <button
                            type="button"
                            disabled={!isCellEditable(row, col)}
                            ref={(element) => {
                              if (element) buttonRefs.current.set(key, element)
                              else buttonRefs.current.delete(key)
                            }}
                            aria-pressed={isSelected}
                            aria-label={`${member.name}, ${entry.weekday} ${entry.month} ${entry.day}, ${formatScheduleLabel(entry.label, format)}${entry.detail ? `, ${entry.detail}` : ''}${isOverstaffedCell ? ', overstaffed' : ''}${isOutsideAvailability ? ', outside stated availability' : ''}${isCellSubmitted(row, col) ? '' : ', Unsubmitted'}`}
                            tabIndex={isFocusable ? 0 : -1}
                            onClick={(event) => activate({ row, col }, event.shiftKey, event.ctrlKey || event.metaKey)}
                            onKeyDown={(event) => handleKeyDown(event, { row, col })}
                            className={`flex min-h-14 w-full flex-col items-center justify-center gap-0.5 px-2 py-1.5 text-center outline-none disabled:pointer-events-none ${periodLocked ? '' : 'disabled:opacity-40'}`}
                          >
                            <span className="text-small font-bold [font-variant-numeric:tabular-nums]">
                              {formatScheduleLabel(entry.label, format)}
                            </span>
                            {entry.detail && <span className="text-caption text-ink-muted">{entry.detail}</span>}
                            {isOverstaffedCell && <span className="rounded bg-error/30 px-1.5 text-caption font-bold text-ink">Over</span>}
                            {isOutsideAvailability && <span className="rounded bg-warning/30 px-1.5 text-caption font-bold text-ink">Unavailable</span>}
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {selected.size > 0 && (
          // z-20, above the table's own sticky headers (z-10): without it,
          // a row's frozen employee name scrolls out from under this bar
          // instead of behind it once the row is near the bottom of the
          // viewport. The negative margins pull it over the page's padding so
          // it (and its sticky offset) meets the bottom and side edges instead of floating above them.
          <div className="sticky -bottom-4 z-20 -mx-4 -mb-4 mt-5 flex-none border-t border-line bg-canvas p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:-bottom-6 sm:-mx-6 sm:-mb-6 lg:-bottom-8 lg:-mx-8 lg:-mb-8">
            <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-2.5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-small font-bold text-ink-muted">
                  {selected.size} {selected.size === 1 ? 'cell' : 'cells'} selected
                </span>
                <div role="group" aria-label="Apply a status" className="flex flex-wrap gap-2">
                  {CATEGORY_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => applyCategory(option)}
                      className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {soleSelectedKey && (
                  <button
                    type="button"
                    aria-expanded={historyOpenFor === soleSelectedKey}
                    onClick={() =>
                      setHistoryOpenFor((current) => (current === soleSelectedKey ? null : soleSelectedKey))
                    }
                    className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2"
                  >
                    Cell history{cellHistory.length > 0 ? ` (${cellHistory.length})` : ''}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelected(new Set())
                    setAnchor(null)
                    setCodeInput('')
                  }}
                  className="ml-auto flex min-h-11 items-center justify-center rounded-control px-3 text-small font-bold text-ink-muted hover:bg-surface-2"
                >
                  Clear selection
                </button>
              </div>

              {historyOpenFor === soleSelectedKey && (
                <div role="region" aria-label="Cell history" className="rounded-surface border border-line bg-surface-2 p-2.5">
                  {cellHistory.length === 0 ? (
                    <p className="text-caption text-ink-subtle">No changes yet this session.</p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {cellHistory.map((entry) => (
                        <li key={`${entry.changedAt}-${entry.changedBy}`} className="text-caption text-ink-muted">
                          <span className="font-semibold text-ink">{entry.changedBy}</span> set{' '}
                          {formatScheduleLabel(entry.label, format)}
                          {entry.detail ? ` · ${entry.detail}` : ''} &middot;{' '}
                          {new Date(entry.changedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="board-code-input" className="text-caption font-bold text-ink-muted">
                  Or type a status or assignment code
                </label>
                <input
                  id="board-code-input"
                  type="text"
                  list="board-codes"
                  value={codeInput}
                  onChange={(event) => setCodeInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      applyCode()
                    }
                  }}
                  placeholder="e.g. Case Processing"
                  className="min-h-11 w-56 rounded-control border border-line-strong px-2.5 text-small"
                />
                <datalist id="board-codes">
                  {BOARD_CODES.map((code) => (
                    <option key={code.code} value={code.code} />
                  ))}
                </datalist>
                <button
                  type="button"
                  onClick={applyCode}
                  disabled={!matchedCode}
                  className="flex min-h-11 items-center justify-center rounded-control bg-primary px-3 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
                >
                  Apply code
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
